import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useAuth } from '@/context/AuthContext';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { ShieldCheck, Check, LogOut, Tag } from 'lucide-react';

const PERKS = [
  'Unlimited AI Coach conversations',
  'Full Brotherhood University learning library',
  'Journal reflections, assessments & growth plans',
  'The private community feed & wellness tools',
];

const PERKS_BY_ROLE = {
  member: PERKS,
  supporter: [
    'Guides for supporting the man in your life',
    'Your own AI Support Coach',
    'Warning-sign & crisis resource tools',
    'The full Brotherhood Support section',
  ],
};

export default function CheckoutStart() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [interval, setInterval] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activating, setActivating] = useState(false);
  const [winback, setWinback] = useState(null);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const autoTriedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    // Capture / persist a win-back token so it survives a login redirect.
    let wbToken = null;
    try {
      const params = new URLSearchParams(window.location.search);
      wbToken = params.get('winback');
      if (wbToken) localStorage.setItem('bp_winback_token', wbToken);
      else wbToken = localStorage.getItem('bp_winback_token');
    } catch { /* ignore */ }

    // Capture a therapist 7-day trial code from the redeem link and persist it so
    // it survives the register/login redirect, then prefill the promo form.
    try {
      const params = new URLSearchParams(window.location.search);
      let trialCode = params.get('code');
      if (trialCode) localStorage.setItem('bp_trial_code', trialCode);
      else trialCode = localStorage.getItem('bp_trial_code');
      if (trialCode) { setPromoCode(trialCode.toUpperCase()); setPromoOpen(true); }
    } catch { /* ignore */ }

    const loadStandard = () => {
      http.get('/paypal/plans')
        .then((r) => {
          if (!alive) return;
          const data = r.data || {};
          const list = data.tiers || [];
          setTiers(list);
          const def = list.find((t) => t.role === data.current_role) || list[0];
          setSelectedTier(def?.tier_id || null);
          let pref = null;
          try { pref = localStorage.getItem('bp_preferred_interval'); } catch { /* ignore */ }
          setInterval((pref === 'annual' || pref === 'monthly') ? pref : (data.default_interval || 'monthly'));
          setLoading(false);
        })
        .catch(() => { if (alive) { setError(true); setLoading(false); } });
    };

    if (wbToken) {
      http.get(`/paypal/winback?token=${encodeURIComponent(wbToken)}`)
        .then((r) => {
          if (!alive) return;
          setWinback(r.data);
          setLoading(false);
        })
        .catch(() => {
          // Invalid/expired offer → clear it and fall back to standard checkout.
          try { localStorage.removeItem('bp_winback_token'); } catch { /* ignore */ }
          if (alive) loadStandard();
        });
    } else {
      loadStandard();
    }
    return () => { alive = false; };
  }, []);

  const onApprove = async (data) => {
    setActivating(true);
    try {
      const r = await http.post('/paypal/subscription/confirm', { subscription_id: data.subscriptionID, promo: appliedPromo?.code });
      if (r.data?.is_active) {
        try { localStorage.removeItem('bp_winback_token'); } catch { /* ignore */ }
        toast.success('Welcome back to Brotherhood+ — your membership is active.');
        try { await refresh(); } catch { /* interceptor refresh handles session */ }
        nav('/app');
      } else {
        toast.info('Your subscription is processing. Refresh in a moment.');
        try { await refresh(); } catch { /* noop */ }
      }
    } catch {
      toast.error('We could not confirm your payment. If you were charged, contact support.');
    } finally {
      setActivating(false);
    }
  };

  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const currentTier = tiers.find((t) => t.tier_id === selectedTier) || null;
  const opt = currentTier?.intervals?.[interval];
  const selectedPlanId = winback
    ? winback.paypal_plan_id
    : (appliedPromo && opt?.promo_plan_id) ? opt.promo_plan_id : opt?.paypal_plan_id;
  const perks = PERKS_BY_ROLE[currentTier?.role] || PERKS;
  const isSupporterCheckout = currentTier?.role === 'supporter';

  const redeemPromo = async (explicit) => {
    const code = (typeof explicit === 'string' ? explicit : promoCode).trim();
    if (!code) return;
    setPromoBusy(true);
    const clearTrial = () => { try { localStorage.removeItem('bp_trial_code'); } catch { /* ignore */ } };
    try {
      // Discount code path: refetch plans with the code so PayPal charges the
      // reduced amount. Falls back to a 100%-free comp code if it isn't a discount.
      const r = await http.get(`/paypal/plans?promo=${encodeURIComponent(code)}`);
      const data = r.data || {};
      if (data.promo) {
        setTiers(data.tiers || []);
        setAppliedPromo(data.promo);
        try { localStorage.removeItem('bp_winback_token'); } catch { /* ignore */ }
        setWinback(null);
        toast.success(`Discount applied — ${data.promo.label}.`);
        setPromoBusy(false);
        return;
      }
      // Not a discount code → try the free/comp redemption flow.
      await http.post('/promo/redeem', { code });
      clearTrial();
      try { localStorage.removeItem('bp_winback_token'); } catch { /* ignore */ }
      toast.success('Promo applied — welcome to Brotherhood+! Your membership is active.');
      try { await refresh(); } catch { /* noop */ }
      nav('/app');
    } catch (e) {
      // The discount lookup 400s for an invalid discount code; try comp redemption
      // as a fallback so a valid free code still works, else show the error.
      try {
        await http.post('/promo/redeem', { code });
        clearTrial();
        toast.success('Promo applied — welcome to Brotherhood+! Your membership is active.');
        try { await refresh(); } catch { /* noop */ }
        nav('/app');
        return;
      } catch (e2) {
        const msg = e2?.response?.data?.detail || e?.response?.data?.detail || 'That promo code could not be applied.';
        if (/already/i.test(msg)) clearTrial();
        toast.error(msg);
      }
    } finally {
      setPromoBusy(false);
    }
  };

  // Auto-apply a stored therapist trial code once the member is authenticated.
  useEffect(() => {
    if (autoTriedRef.current || loading || !user || user.is_premium) return;
    let stored = null;
    try { stored = localStorage.getItem('bp_trial_code'); } catch { /* ignore */ }
    if (!stored) return;
    autoTriedRef.current = true;
    redeemPromo(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  return (
    <div className="min-h-screen bg-navy text-white flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white text-navy rounded-3xl p-7 lg:p-8 card-soft" data-testid="paywall-card">
        <div className="flex items-center gap-2 text-gold">
          <ShieldCheck size={18} strokeWidth={2} />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">
            {winback ? 'Welcome-back offer' : (isSupporterCheckout ? 'Brotherhood Support' : 'Membership')}
          </span>
        </div>
        <h1 className="font-heading text-3xl mt-3">
          {winback
            ? (winback.product_name || 'Come back to Brotherhood+')
            : (currentTier?.product_name || (isSupporterCheckout ? 'Keep showing up for him' : 'Join Brotherhood+'))}
        </h1>
        <p className="text-navy/60 text-sm mt-2 leading-relaxed">
          {winback
            ? `We'd love to have you back — enjoy ${winback.discount_pct}% off your first month.`
            : (isSupporterCheckout
                ? 'Your account is ready — activate to keep supporting the man you care about, with your Support Coach, the full guide library, and crisis resources.'
                : 'Your account is ready — activate your membership to unlock everything inside.')}
        </p>

        {winback ? (
          <div className="mt-5 rounded-2xl bg-navy/5 p-4" data-testid="winback-offer">
            <div className="flex items-center gap-2">
              <span className="bg-gold text-navy text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1" data-testid="winback-discount-badge">
                {winback.discount_pct}% off
              </span>
              <span className="text-xs text-navy/60">first month</span>
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-heading text-4xl" data-testid="winback-first-price">{winback.first_display}</span>
              <span className="text-navy/50 text-sm">first month</span>
            </div>
            <p className="text-xs text-navy/55 mt-1" data-testid="winback-regular-note">
              then {winback.regular_display}{winback.label || '/month'} · cancel anytime
            </p>
          </div>
        ) : (
          <>
            {/* Choose a membership tier */}
            {tiers.length > 1 && (
              <div className="mt-5 space-y-2" role="radiogroup" aria-label="Choose membership" data-testid="tier-selector">
                {tiers.map((t) => {
                  const m = t.intervals?.monthly;
                  const active = t.tier_id === selectedTier;
                  const popular = t.role === 'member';
                  return (
                    <button
                      key={t.tier_id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setSelectedTier(t.tier_id)}
                      data-testid={`tier-select-${t.role}`}
                      className={`relative w-full text-left rounded-2xl border p-3.5 transition-smooth flex items-center justify-between gap-3 ${active ? 'border-gold ring-2 ring-gold/40 bg-gold/5' : 'border-navy/10 hover:border-navy/25'}`}
                    >
                      {popular && (
                        <span
                          className="absolute -top-2 right-3 bg-gold text-navy text-[9px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 shadow-sm"
                          data-testid="tier-popular-badge"
                        >
                          Most popular
                        </span>
                      )}
                      <div className="min-w-0">
                        <p className="font-heading text-base leading-tight">{t.product_name}</p>
                        <p className="text-navy/55 text-xs">{t.description}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-heading text-lg leading-none">{m?.display}</div>
                        <div className="text-navy/50 text-[11px]">{m?.label}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Monthly / Annual toggle */}
            {currentTier?.intervals && (
              <div className="mt-4 grid grid-cols-2 gap-2 bg-navy/5 rounded-2xl p-1" data-testid="interval-toggle">
                {['monthly', 'annual'].map((iv) => (
                  <button
                    key={iv}
                    onClick={() => setInterval(iv)}
                    className={`relative rounded-xl py-2 text-sm font-semibold transition-smooth ${interval === iv ? 'bg-navy text-white' : 'text-navy/60 hover:text-navy'}`}
                    data-testid={`interval-${iv}`}
                  >
                    {iv === 'monthly' ? 'Monthly' : 'Annual'}
                    {iv === 'annual' && (
                      <span className="absolute -top-2 -right-1 bg-gold text-navy text-[9px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 shadow-sm" data-testid="annual-savings-badge">
                        2 months free
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            <div className="mt-5 flex items-baseline gap-1">
              {appliedPromo && opt?.promo ? (
                <>
                  <span className="font-heading text-4xl text-gold" data-testid="paywall-price">{opt.promo.discounted_display}</span>
                  <span className="text-navy/50 text-sm">{opt.label}</span>
                  <span className="ml-2 text-navy/40 text-sm line-through" data-testid="paywall-price-original">{opt.promo.original_display}</span>
                </>
              ) : (
                <>
                  <span className="font-heading text-4xl" data-testid="paywall-price">{opt?.display || '$14.99'}</span>
                  <span className="text-navy/50 text-sm">{opt?.label || '/month'}</span>
                </>
              )}
            </div>
            {appliedPromo && opt?.promo ? (
              <p className="text-xs text-gold font-semibold mt-1" data-testid="paywall-promo-note">
                {appliedPromo.label} · {opt.promo.note}
              </p>
            ) : (
              opt?.note && <p className="text-xs text-gold font-semibold mt-1" data-testid="paywall-note">{opt.note}</p>
            )}
          </>
        )}

        <p className="mt-5 text-xs font-semibold uppercase tracking-[0.15em] text-navy/45" data-testid="paywall-unlock-heading">
          {winback ? 'Your membership includes' : "What you'll unlock"}
        </p>
        <ul className="mt-2.5 space-y-2.5">
          {perks.map((p) => (
            <li key={p} className="flex items-start gap-2.5 text-sm text-navy/80">
              <Check size={16} className="text-gold mt-0.5 shrink-0" strokeWidth={2.5} />
              <span>{p}</span>
            </li>
          ))}
        </ul>

        <div className="mt-7 min-h-[52px]" data-testid="paypal-button-container">
          {loading && <div className="h-[52px] rounded-xl bg-navy/5 animate-pulse" />}
          {error && (
            <div className="text-center">
              <p className="text-sm text-red-600 mb-3">Payments are temporarily unavailable.</p>
              <button onClick={() => window.location.reload()} className="inline-flex items-center justify-center min-h-[44px] px-3 text-sm underline text-navy/70" data-testid="paywall-retry">
                Try again
              </button>
            </div>
          )}
          {!loading && !error && selectedPlanId && clientId && (
            <PayPalScriptProvider options={{ clientId, vault: true, intent: 'subscription' }}>
              <PayPalButtons
                style={{ layout: 'vertical', color: 'gold', shape: 'pill', label: 'subscribe' }}
                disabled={activating}
                forceReRender={[selectedPlanId]}
                createSubscription={(d, actions) => actions.subscription.create({ plan_id: selectedPlanId })}
                onApprove={onApprove}
                onError={() => toast.error('PayPal ran into a problem. Please try again.')}
              />
            </PayPalScriptProvider>
          )}
        </div>

        <p className="text-[11px] text-navy/45 leading-relaxed mt-4 text-center">
          Secure recurring billing by PayPal. Cancel anytime from your profile — you keep access through the paid period.
        </p>

        {/* Promo code */}
        <div className="mt-4 pt-4 border-t border-navy/10">
          {!promoOpen ? (
            <button
              onClick={() => setPromoOpen(true)}
              className="inline-flex items-center gap-1.5 min-h-[44px] text-sm text-navy/60 hover:text-navy transition-smooth"
              data-testid="promo-toggle"
            >
              <Tag size={14} /> Have a promo code?
            </button>
          ) : (
            <div data-testid="promo-form">
              <label className="text-xs font-semibold uppercase tracking-wide text-navy/50">Promo code</label>
              <div className="mt-1.5 flex gap-2">
                <input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === 'Enter') redeemPromo(); }}
                  placeholder="Enter code"
                  className="flex-1 rounded-xl border border-navy/15 px-3 py-2.5 text-sm uppercase tracking-wide focus:outline-none focus:border-navy"
                  data-testid="promo-input"
                  autoFocus
                />
                <button
                  onClick={redeemPromo}
                  disabled={promoBusy || !promoCode.trim()}
                  className="rounded-xl bg-navy text-white text-sm font-semibold px-4 py-2.5 disabled:opacity-50 transition-smooth"
                  data-testid="promo-apply"
                >
                  {promoBusy ? 'Applying…' : 'Apply'}
                </button>
              </div>
              <p className="text-[11px] text-navy/45 mt-1.5">A valid code unlocks your membership instantly — no payment needed.</p>
            </div>
          )}
        </div>
      </div>

      <button
        onClick={() => nav('/app')}
        className="mt-6 inline-flex items-center justify-center min-h-[44px] px-3 text-white/80 hover:text-white text-sm font-medium underline underline-offset-4 transition-smooth"
        data-testid="paywall-skip"
      >
        Maybe later — start my 7-day free trial
      </button>
      <p className="mt-2 text-white/45 text-xs text-center max-w-xs" data-testid="paywall-skip-note">
        Your free trial gives you a taste for 7 days. Membership above unlocks everything, for good.
      </p>

      <button
        onClick={() => { logout(); nav('/login'); }}
        className="mt-3 inline-flex items-center gap-1.5 min-h-[44px] px-3 text-white/60 hover:text-white text-sm transition-smooth"
        data-testid="paywall-signout"
      >
        <LogOut size={14} /> Sign out{user?.email ? ` (${user.email})` : ''}
      </button>
    </div>
  );
}
